import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { FaChevronLeft, FaChevronRight, FaPlay, FaFilm } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { MdStar } from 'react-icons/md';
import './neon-movie-list.scss';

const TMDB_KEY = '0623e0d232ae90274f2d77494ac4ce50';

async function fetchTrailerKey(item) {
    if (item.id) {
        try {
            const r = await fetch(`/movies-trailer-api.php?movieId=${item.id}`);
            const d = await r.json();
            const m = (d?.data?.trailerUrl || '').match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
            if (m) return m[1];
        } catch {}
    }
    const stored = (item.trailerUrl || '').match(/(?:youtube\.com\/(?:watch\?.*v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (stored) return stored[1];
    try {
        const res = await fetch(`https://api.themoviedb.org/3/search/movie?api_key=${TMDB_KEY}&query=${encodeURIComponent(item.title)}`);
        const d = await res.json();
        if (d.results?.length) {
            const vr = await fetch(`https://api.themoviedb.org/3/movie/${d.results[0].id}/videos?api_key=${TMDB_KEY}`);
            const vd = await vr.json();
            const t = vd.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube') || vd.results?.find(v => v.site === 'YouTube');
            if (t) return t.key;
        }
    } catch {}
    try {
        const res = await fetch(`https://api.themoviedb.org/3/search/tv?api_key=${TMDB_KEY}&query=${encodeURIComponent(item.title)}`);
        const d = await res.json();
        if (d.results?.length) {
            const vr = await fetch(`https://api.themoviedb.org/3/tv/${d.results[0].id}/videos?api_key=${TMDB_KEY}`);
            const vd = await vr.json();
            const t = vd.results?.find(v => v.type === 'Trailer' && v.site === 'YouTube') || vd.results?.find(v => v.site === 'YouTube');
            if (t) return t.key;
        }
    } catch {}
    return null;
}

function TrailerModal({ item, onClose }) {
    const [key, setKey] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchTrailerKey(item).then(k => { setKey(k); setLoading(false); });
        const onKey = e => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [item, onClose]);

    return ReactDOM.createPortal(
        <div className="ec-overlay" onClick={onClose}>
            <div className="ec-modal" onClick={e => e.stopPropagation()}>
                <div className="ec-modal__head">
                    <span className="ec-modal__label"><FaFilm size={10} /> Trailer</span>
                    <h3 className="ec-modal__title">{item.title}</h3>
                    <button className="ec-modal__close" onClick={onClose}><IoClose size={20} /></button>
                </div>
                {loading
                    ? <div className="ec-modal__state"><div className="ec-spinner" /><span>Đang tải...</span></div>
                    : key
                        ? <div className="ec-modal__player">
                            <iframe
                                src={`https://www.youtube.com/embed/${key}?autoplay=1&rel=0`}
                                allow="autoplay; encrypted-media; fullscreen"
                                allowFullScreen
                                title={item.title}
                            />
                          </div>
                        : <div className="ec-modal__state"><span>Không tìm thấy trailer.</span></div>
                }
            </div>
        </div>,
        document.body
    );
}

const NeonMovieList = ({ genreId, size = 100 }) => {
    const [items, setItems]           = useState([]);
    const [loading, setLoading]       = useState(true);
    const [active, setActive]         = useState(null);
    const [isBeginning, setIsBeginning] = useState(true);
    const [isEnd, setIsEnd]           = useState(false);
    const swiperRef = useRef(null);
    const dragging  = useRef(false);

    useEffect(() => {
        const url = genreId
            ? `/movies-public-api.php?type=trailer&genreId=${genreId}&page=0&size=${size}`
            : `/movies-public-api.php?type=trailer&page=0&size=${size}`;
        setLoading(true);
        fetch(url)
            .then(r => r.json())
            .then(d => { if (d.success) setItems(d.content || []); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [genreId, size]);

    if (loading) return <div className="ec-loading">Đang tải...</div>;
    if (!items.length) return null;

    const updateNav = s => { setIsBeginning(s.isBeginning); setIsEnd(s.isEnd); };

    const slidePrev = () => {
        const s = swiperRef.current;
        if (!s) return;
        const n = s.slidesPerViewDynamic ? s.slidesPerViewDynamic() : 1;
        s.slideTo(Math.max(0, s.activeIndex - n));
    };
    const slideNext = () => {
        const s = swiperRef.current;
        if (!s) return;
        const n = s.slidesPerViewDynamic ? s.slidesPerViewDynamic() : 1;
        s.slideTo(s.activeIndex + n);
    };

    return (
        <>
            <div className="ec-list">
                {!isBeginning && (
                    <button className="ec-nav ec-nav--prev" onClick={slidePrev}>
                        <FaChevronLeft size={12} />
                    </button>
                )}
                {!isEnd && (
                    <button className="ec-nav ec-nav--next" onClick={slideNext}>
                        <FaChevronRight size={12} />
                    </button>
                )}
                <Swiper
                    grabCursor
                    spaceBetween={10}
                    slidesPerView="auto"
                    touchStartPreventDefault={false}
                    preventClicks={false}
                    preventClicksPropagation={false}
                    onSwiper={s => { swiperRef.current = s; updateNav(s); }}
                    onSlideChange={updateNav}
                    onReachBeginning={() => { setIsBeginning(true); setIsEnd(false); }}
                    onReachEnd={() => { setIsEnd(true); setIsBeginning(false); }}
                    onFromEdge={s => updateNav(s)}
                    onTouchStart={() => { dragging.current = false; }}
                    onTouchMove={() => { dragging.current = true; }}
                >
                    {items.map((item, i) => {
                        const thumb  = item.thumbnailUrl || item.posterUrl;
                        const genre  = item.genres?.[0]?.name || '';
                        const rating = item.averageRating ? parseFloat(item.averageRating).toFixed(1) : null;

                        return (
                            <SwiperSlide key={item.id} className="ec-slide">
                                <div
                                    className="ec-card"
                                    onClick={() => { if (!dragging.current) setActive(item); }}
                                >
                                    <img
                                        className="ec-poster"
                                        src={thumb}
                                        alt={item.title}
                                        loading="lazy"
                                        onError={e => { e.target.style.opacity = '0'; }}
                                    />

                                    <div className="ec-vignette" />

                                    <span className="ec-badge">Trailer</span>

                                    {rating && (
                                        <span className="ec-score">
                                            <MdStar size={11} />
                                            {rating}
                                        </span>
                                    )}

                                    <div className="ec-play">
                                        <FaPlay size={14} />
                                    </div>

                                    <div className="ec-title-static">
                                        <h3>{item.title}</h3>
                                    </div>

                                    <div className="ec-info">
                                        {genre && <span className="ec-genre">{genre}</span>}
                                        <h3 className="ec-info-title">{item.title}</h3>
                                        <div className="ec-meta">
                                            {item.country    && <span>{item.country}</span>}
                                            {item.releaseYear && <span>{item.releaseYear}</span>}
                                        </div>
                                    </div>
                                </div>
                            </SwiperSlide>
                        );
                    })}
                </Swiper>
            </div>

            {active && <TrailerModal item={active} onClose={() => setActive(null)} />}
        </>
    );
};

export default NeonMovieList;
